import React, {
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  PaginationState as ReactTablePaginationState,
} from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import SummaryCard, {
  SummaryCardItem,
} from 'core/components/SummaryCard';
import MonthPicker from 'core/components/DateTimePicker/MonthPicker';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  maxBillingCodeSearchQueryLength,
} from 'core/constants';
import DataProvider, {
  DataContext,
} from 'core/context/data';
import Filters from 'core/Filters';
import compact from 'lodash/compact';
import {
  useFilterQuery,
  useMedia,
  useTableReset,
} from 'core/hooks';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import SearchIcon from 'core/icons/search';
import useStorageState from 'core/hooks/storage';
import {
  useSyncSearchQuery,
} from 'pages/Dashboard/hooks/url';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import {
  CcmMonthlyPatientsRequestDTO,
  CcmMonthlyPatientsResponseDTO,
  CcmMonthlySummaryDTO,
} from 'dtos';
import {
  postApiCcmMonthlyDashboardGetPatients,
} from 'endpoints';
import {
  columns,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/columns';
import CcmCallDetailDialog from 'pages/Dashboard/pages/Dashboard/CcmMonthly/CcmCallDetailDialog';
import normalizeCcmMonthlyRows from 'pages/Dashboard/pages/Dashboard/CcmMonthly/helper';
import {
  CcmMonthlyCallRow,
  CcmMonthlyRow,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/types';
import {
  formatDateOnly,
} from 'utils/date';
import dayjs from 'utils/dayjs';

type CcmMonthlyDashboardFilterQuery = {
  claimStatusList?: string[] | null;
  callAttemptRange?: {
    min?: number | null;
    max?: number | null;
  } | null;
};

type OverviewProps = {
  tableInstanceRef: RefObject<VirtualTableInstance>;
};

function measureValue(measure?: { current?: number }): number {
  return measure?.current ?? 0;
}

function getSummaryItems(summary?: CcmMonthlySummaryDTO, patientsCount = 0): SummaryCardItem[] {
  const patientsContactedValue = Math.max(
    measureValue(summary?.patientsContacted),
    patientsCount,
  );

  return [
    {
      id: 'patientsContacted',
      label: 'Patients contacted',
      value: patientsContactedValue,
      tone: 'info',
    },
    {
      id: 'aiCallAttempts',
      label: 'Call attempts',
      value: measureValue(summary?.aiCallAttempts),
      tone: 'default',
    },
  ];
}

function Overview({ tableInstanceRef }: OverviewProps) {
  const { data, auxiliary } = useContext(DataContext);
  const [summary, setSummary] = useState<CcmMonthlySummaryDTO | undefined>();
  const [selectedCall, setSelectedCall] = useState<CcmMonthlyCallRow | null>(null);
  const patientsCount = useMemo(() => (
    (data as CcmMonthlyRow[])?.filter((row) => !row.isCallDetail).length ?? 0
  ), [data]);

  const summaryItems = useMemo(
    () => getSummaryItems(summary, patientsCount),
    [summary, patientsCount],
  );
  const tableMeta = useMemo(() => ({
    onOpenCallDetail: setSelectedCall,
  }), []);
  const { canRequestMultimedia } = useEMRAccess();
  const { requestMedia } = useMedia();

  useEffect(() => {
    if (!isNil(auxiliary)) {
      setSummary(auxiliary as CcmMonthlySummaryDTO);
    }
  }, [auxiliary]);

  useEffect(() => {
    if (canRequestMultimedia && !isNil(data)) {
      const patientIds = compact(
        (data ?? []).map((row) => ('patientId' in row ? row.patientId : undefined)),
      );
      if (patientIds.length > 0) {
        requestMedia(patientIds);
      }
  }
  }, [data, canRequestMultimedia, requestMedia]);

  return (
    <div className="relative flex flex-col flex-1 overflow-y-hidden text-gray-600">
      <div className="px-4 pb-3">
        <SummaryCard
          items={summaryItems}
          isLoading={isNil(summary)}
          skeletonItemCount={summaryItems.length}
        />
      </div>
      <div className="w-full flex-1 min-h-0">
        <Table<CcmMonthlyRow>
          instanceRef={tableInstanceRef}
          columns={columns}
          meta={tableMeta}
          getRowId={({ id }) => id}
          getSubRows={(row) => ('calls' in row ? row.calls : undefined)}
          getRowCanExpand={({ original }) => !original.isCallDetail && original.expandable}
          onRowClick={(_, row) => {
            if (row.getCanExpand()) {
              row.toggleExpanded();
            }
          }}
          emptySlot="No CCM patients found"
          loadingSlot="Loading CCM patients..."
          classes={{
            header: '!justify-start px-4',
            outerColumn: '!items-start',
            column: '!text-left',
            scrollContainer: 'justify-center items-center',
            outerRow: '!w-[calc(100%-16px)] left-4',
            row: '!items-stretch !pt-1 !justify-start',
            cell: ({ original }) => clsx('!items-start !h-auto', {
              '!py-2': !original.isCallDetail,
              '!py-3': original.isCallDetail,
            }),
            subRow: ({ original }) => clsx('!border-t !pb-2', {
              '!border-blue-100 !bg-blue-50/40': original.isCallDetail,
              '!border-gray-200': !original.isCallDetail,
            }),
          }}
        />
      </div>
      <CcmCallDetailDialog
        callInfo={selectedCall}
        onClose={() => { setSelectedCall(null); }}
      />
    </div>
  );
}

const defaultReportingMonth = formatDateOnly(dayjs().startOf('month'));

export default function CcmMonthly() {
  const {
    reset,
    tableInstanceRef,
  } = useTableReset();
  const {
    searchQuery,
    setSearchQuery,
  } = useSyncSearchQuery({ storageKey: 'ccmMonthlySearchQuery' });
  const [reportingMonth, setReportingMonth] = useStorageState<string>(
    'ccmMonthlyReportingMonth',
    defaultReportingMonth,
  );
  const {
    filterQuery,
    setFilterQuery,
    filterInstanceRef,
  } = useFilterQuery<CcmMonthlyDashboardFilterQuery>(
    'ccmMonthlyDashboard',
    true,
  );
  const [defaultPagination] = useStorageState<ReactTablePaginationState>('ccmMonthlyPagination', {
    pageSize: 25,
    pageIndex: 0,
  });

  function onFilterUpdate() {
    setFilterQuery(filterInstanceRef.current?.getFilterQuery?.() ?? {});
    reset();
  }

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <div className="flex gap-4 items-center px-4">
        <SearchInput
          placeholder="Search patient name"
          className="w-96"
          delay={400}
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            reset();
          }}
          inputProps={{ autoComplete: 'off', maxLength: maxBillingCodeSearchQueryLength }}
          icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
          id="ccm-monthly-search"
        />
        <MonthPicker
          value={dayjs(reportingMonth)}
          placeholder="MM/YYYY"
          onChange={(value) => {
            if (!isNil(value)) {
              setReportingMonth(formatDateOnly(value.startOf('month')));
              reset();
            }
          }}
        />
        <Filters
          pageObject="ccmMonthlyDashboard"
          onFilterUpdate={onFilterUpdate}
          instanceRef={filterInstanceRef}
          useStorage
        />
      </div>
      <DataProvider<
        CcmMonthlyPatientsResponseDTO,
        CcmMonthlyRow,
        CcmMonthlySummaryDTO
      >
        queryKey="ccm-monthly-patients"
        pagination={defaultPagination}
        getData={(request: CcmMonthlyPatientsRequestDTO) => (
          postApiCcmMonthlyDashboardGetPatients(request)
        )}
        getMeta={(response) => (response?.patients?.pageProperties ?? {})}
        getAuxiliary={(response) => response?.summary}
        getQuery={({
          reportingMonth: queryReportingMonth,
          searchQuery: querySearch,
          filterQuery: queryFilter,
          pagination,
        }) => ({
          reportingMonth: queryReportingMonth,
          searchText: querySearch,
          filterQuery: queryFilter,
          pageRequest: {
            page: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? 25,
          },
        } satisfies CcmMonthlyPatientsRequestDTO)}
        normalize={(response) => normalizeCcmMonthlyRows(response?.patients?.data ?? [])}
        params={{
          reportingMonth,
          searchQuery,
          filterQuery,
        }}
      >
        <Overview tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>} />
      </DataProvider>
    </div>
  );
}
